import { tiendaDb } from '../lib/supabase';

// ─── ROTATION THRESHOLDS (configurable) ──────────────────────────────────────
export const ROTATION_CONFIG = {
  alta: 0.5,       // > 0.5 units/day = HIGH rotation
  media: 0.1,      // 0.1–0.5 = MEDIUM
  // < 0.1 = LOW, 0 = NO MOVEMENT
  cobertura: {
    riesgoRotura: 7,    // < 7 days = RISK
    stockCorrecto: 45,  // 7–45 = OK
    // > 45 = OVER-STOCK
  },
  sinMovimiento: {
    dias30: 30,
    dias60: 60,
    dias90: 90,
    dias180: 180,
  },
  roturaReal: {
    diasRecientes: 60, // product must have sold in last 60 days to be "real stockout"
  },
};

// ─── DATE HELPERS ─────────────────────────────────────────────────────────────
export const getDateRange = (periodo, customDesde = null, customHasta = null) => {
  const now = new Date();
  const today = now?.toISOString()?.split('T')?.[0];

  const fmt = (d) => d?.toISOString()?.split('T')?.[0];
  const startOf = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

  switch (periodo) {
    case 'hoy':
      return { desde: today, hasta: today };
    case 'ayer': {
      const y = new Date(now); y?.setDate(y?.getDate() - 1);
      const ys = fmt(y);
      return { desde: ys, hasta: ys };
    }
    case 'semana': {
      const dow = now?.getDay() === 0 ? 6 : now?.getDay() - 1;
      const mon = new Date(now); mon?.setDate(now?.getDate() - dow);
      return { desde: fmt(startOf(mon)), hasta: today };
    }
    case 'semana_anterior': {
      const dow = now?.getDay() === 0 ? 6 : now?.getDay() - 1;
      const mon = new Date(now); mon?.setDate(now?.getDate() - dow - 7);
      const sun = new Date(mon); sun?.setDate(mon?.getDate() + 6);
      return { desde: fmt(startOf(mon)), hasta: fmt(startOf(sun)) };
    }
    case 'mes': {
      const first = new Date(now.getFullYear(), now.getMonth(), 1);
      return { desde: fmt(first), hasta: today };
    }
    case 'mes_anterior': {
      const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const last = new Date(now.getFullYear(), now.getMonth(), 0);
      return { desde: fmt(first), hasta: fmt(last) };
    }
    case 'trimestre': {
      const q = Math.floor(now?.getMonth() / 3);
      const first = new Date(now.getFullYear(), q * 3, 1);
      return { desde: fmt(first), hasta: today };
    }
    case 'anio': {
      return { desde: `${now?.getFullYear()}-01-01`, hasta: today };
    }
    case 'anio_anterior': {
      const y = now?.getFullYear() - 1;
      return { desde: `${y}-01-01`, hasta: `${y}-12-31` };
    }
    case 'personalizado':
      return { desde: customDesde || today, hasta: customHasta || today };
    default:
      return { desde: fmt(new Date(now.getFullYear(), now.getMonth(), 1)), hasta: today };
  }
};

export const getPreviousPeriod = (desde, hasta) => {
  const d1 = new Date(desde);
  const d2 = new Date(hasta);
  const diffMs = d2 - d1 + 86400000;
  const prevHasta = new Date(d1 - 86400000);
  const prevDesde = new Date(prevHasta - diffMs + 86400000);
  const fmt = (d) => d?.toISOString()?.split('T')?.[0];
  return { desde: fmt(prevDesde), hasta: fmt(prevHasta) };
};

export const getSamePeriodLastYear = (desde, hasta) => {
  const d1 = new Date(desde);
  const d2 = new Date(hasta);
  d1?.setFullYear(d1?.getFullYear() - 1);
  d2?.setFullYear(d2?.getFullYear() - 1);
  const fmt = (d) => d?.toISOString()?.split('T')?.[0];
  return { desde: fmt(d1), hasta: fmt(d2) };
};

export const getDaysDiff = (desde, hasta) => {
  const d1 = new Date(desde);
  const d2 = new Date(hasta);
  return Math.max(1, Math.round((d2 - d1) / 86400000) + 1);
};

// ─── GRANULARITY HELPER ───────────────────────────────────────────────────────
export const getGranularidad = (desde, hasta) => {
  const days = getDaysDiff(desde, hasta);
  if (days <= 1) return 'hora';
  if (days <= 92) return 'dia';
  return 'mes';
};

// ─── MAIN KPIs ────────────────────────────────────────────────────────────────
export const getVentasKpis = async (desde, hasta) => {
  const { data, error } = await tiendaDb?.rpc('get_dashboard_ventas_kpis', {
    p_desde: desde,
    p_hasta: hasta,
  });
  if (error) throw error;
  return data?.[0] || {
    total_ventas: 0,
    total_unidades: 0,
    total_kg: 0,
    n_tickets: 0,
    ticket_medio: 0,
    coste_vendido: 0,
    beneficio: 0,
    margen_pct: 0,
    n_lineas: 0,
    n_lineas_vinculadas: 0,
  };
};

// ─── STOCK VALORADO ───────────────────────────────────────────────────────────
export const getStockValorado = async (familiaId = null, proveedorId = null) => {
  const { data, error } = await tiendaDb?.rpc('get_dashboard_stock_valorado', {
    p_familia_id: familiaId || null,
    p_proveedor_id: proveedorId || null,
  });
  if (error) throw error;
  return data?.[0] || {
    stock_coste: 0, stock_pvp: 0, beneficio_potencial: 0,
    n_referencias: 0, n_stock_negativo: 0, n_sin_coste: 0, n_sin_pvp: 0,
  };
};

// ─── COMPRAS KPIs ─────────────────────────────────────────────────────────────
export const getComprasKpis = async (desde, hasta) => {
  const today = new Date()?.toISOString()?.split('T')?.[0];
  const { data, error } = await tiendaDb?.from('facturas_compra')?.select('total, pendiente_pago, estado_pago, fecha_factura, fecha_vencimiento')?.gte('fecha_factura', desde)?.lte('fecha_factura', hasta)?.neq('estado_pago', 'ANULADA');
  if (error) throw error;

  const all = data || [];
  const totalCompras = all?.reduce((s, f) => s + (f?.total || 0), 0);

  // Pending and overdue (all time, not just period)
  const { data: pendData, error: pendErr } = await tiendaDb?.from('facturas_compra')?.select('pendiente_pago, fecha_vencimiento, estado_pago')?.neq('estado_pago', 'ANULADA')?.neq('estado_pago', 'PAGADA');
  if (pendErr) throw pendErr;

  const pendientes = pendData || [];
  const totalPendiente = pendientes?.reduce((s, f) => s + (f?.pendiente_pago || 0), 0);
  const vencidas = pendientes?.filter(f => f?.fecha_vencimiento && f?.fecha_vencimiento < today);
  const importeVencido = vencidas?.reduce((s, f) => s + (f?.pendiente_pago || 0), 0);

  return {
    total_compras: totalCompras,
    n_facturas: all?.length,
    pendiente_pago: totalPendiente,
    n_facturas_vencidas: vencidas?.length,
    importe_vencido: importeVencido,
  };
};

// ─── EVOLUCIÓN TEMPORAL ───────────────────────────────────────────────────────
export const getEvolucion = async (desde, hasta) => {
  const granularidad = getGranularidad(desde, hasta);
  const { data, error } = await tiendaDb?.rpc('get_dashboard_evolucion', {
    p_desde: desde,
    p_hasta: hasta,
    p_granularidad: granularidad,
  });
  if (error) throw error;
  return { data: data || [], granularidad };
};

// ─── RANKING DE PRODUCTOS ─────────────────────────────────────────────────────
export const getRankingProductos = async (desde, hasta, familiaId = null, proveedorId = null, limit = 50) => {
  const { data, error } = await tiendaDb?.rpc('get_dashboard_ranking_productos', {
    p_desde: desde,
    p_hasta: hasta,
    p_familia_id: familiaId || null,
    p_proveedor_id: proveedorId || null,
    p_limit: limit,
  });
  if (error) throw error;
  return data || [];
};

// ─── ANÁLISIS POR FAMILIA ─────────────────────────────────────────────────────
export const getFamilias = async (desde, hasta) => {
  const { data, error } = await tiendaDb?.rpc('get_dashboard_familias', {
    p_desde: desde,
    p_hasta: hasta,
  });
  if (error) throw error;
  return data || [];
};

// ─── COMPRAS POR PROVEEDOR ────────────────────────────────────────────────────
// IMPORTANT: always pass desde/hasta so the RPC respects the global period filter
export const getComprasProveedor = async (desde, hasta) => {
  const { data, error } = await tiendaDb?.rpc('get_dashboard_compras_proveedor', {
    p_desde: desde,
    p_hasta: hasta,
  });
  if (error) throw error;
  return data || [];
};

// ─── DIAGNÓSTICO DE VINCULACIÓN ───────────────────────────────────────────────
export const getVinculacionDiagnostico = async () => {
  const { data, error } = await tiendaDb?.rpc('get_dashboard_vinculacion_diagnostico');
  if (error) throw error;
  return data?.[0] || {
    total_lineas: 0,
    lineas_con_producto: 0,
    lineas_sin_producto: 0,
    pct_vinculado: 0,
    codigos_sin_vincular: '',
  };
};

// ─── CALIDAD DE DATOS ─────────────────────────────────────────────────────────
export const getCalidadDatos = async () => {
  const { data, error } = await tiendaDb?.rpc('get_dashboard_calidad_datos');
  if (error) throw error;
  return data?.[0] || {
    sin_coste: 0, sin_pvp: 0, sin_familia: 0, sin_proveedor: 0,
    stock_negativo: 0, epelsa_no_vinculado: 0, total_issues: 0,
  };
};

// ─── ROTACIÓN Y ANÁLISIS DE INVENTARIO ───────────────────────────────────────
// Client-side calculation using pre-fetched ranking data + inventory
export const calcularRotacion = (productos, diasPeriodo) => {
  return productos?.map(p => {
    const velocidad = diasPeriodo > 0 ? (p?.unidades || 0) / diasPeriodo : 0;
    const stock = Math.max(p?.stock_actual || 0, 0);
    const cobertura = velocidad > 0 ? stock / velocidad : null;
    const diasSinVender = p?.ultima_venta
      ? Math.floor((Date.now() - new Date(p.ultima_venta)) / 86400000)
      : null;

    let clasificacion;
    if (velocidad === 0) clasificacion = 'SIN_MOVIMIENTO';
    else if (velocidad >= ROTATION_CONFIG?.alta) clasificacion = 'ALTA';
    else if (velocidad >= ROTATION_CONFIG?.media) clasificacion = 'MEDIA';
    else clasificacion = 'BAJA';

    let coberturaClasif;
    if (cobertura === null) coberturaClasif = 'SIN_DATOS';
    else if (cobertura <= ROTATION_CONFIG?.cobertura?.riesgoRotura) coberturaClasif = 'RIESGO_ROTURA';
    else if (cobertura <= ROTATION_CONFIG?.cobertura?.stockCorrecto) coberturaClasif = 'CORRECTO';
    else coberturaClasif = 'SOBRE_STOCK';

    return {
      ...p,
      velocidad_dia: velocidad,
      cobertura_dias: cobertura,
      clasificacion_rotacion: clasificacion,
      clasificacion_cobertura: coberturaClasif,
      dias_sin_vender: diasSinVender,
    };
  });
};

// ─── CLASIFICACIÓN ABC ────────────────────────────────────────────────────────
export const calcularABC = (productos, criterio = 'ventas_eur') => {
  const sorted = [...productos]?.sort((a, b) => (b?.[criterio] || 0) - (a?.[criterio] || 0));
  const total = sorted?.reduce((s, p) => s + (p?.[criterio] || 0), 0);
  let acumulado = 0;

  return sorted?.map(p => {
    acumulado += (p?.[criterio] || 0);
    const pctAcumulado = total > 0 ? (acumulado / total) * 100 : 0;
    let abc;
    if (pctAcumulado <= 80) abc = 'A';
    else if (pctAcumulado <= 95) abc = 'B';
    else abc = 'C';
    return { ...p, abc, pct_acumulado: pctAcumulado };
  });
};

// ─── PRODUCTOS SIN MOVIMIENTO ─────────────────────────────────────────────────
export const getProductosSinMovimiento = async (diasSinVenta = 90) => {
  const cutoff = new Date();
  cutoff?.setDate(cutoff?.getDate() - diasSinVenta);
  const cutoffStr = cutoff?.toISOString()?.split('T')?.[0];

  // Get all active products with inventory
  const { data: invData, error: invErr } = await tiendaDb?.from('inventario_actual')?.select(`
      stock_actual, coste_medio,
      producto:productos(
        id, nombre, activo, pvp_venta, precio_compra,
        categoria:categorias(id, nombre, familia_id,
          familia:familias_producto(id, nombre)
        ),
        proveedor:proveedores(id, nombre)
      )
    `)?.gt('stock_actual', 0);
  if (invErr) throw invErr;

  // Get last sale date per product from epelsa
  const { data: ventasData, error: ventasErr } = await tiendaDb?.from('epelsa_ticket_lineas')?.select(`
      articulo_codigo,
      ticket:epelsa_tickets(fecha_operacion)
    `)?.gte('ticket.fecha_operacion', cutoffStr)?.order('ticket.fecha_operacion', { ascending: false });
  if (ventasErr) throw ventasErr;

  // Get product IDs that have sold recently via epelsa_articulos
  const { data: artData, error: artErr } = await tiendaDb?.from('epelsa_articulos')?.select('codigo, producto_id');
  if (artErr) throw artErr;

  const codigosConVenta = new Set((ventasData || []).map(v => v.articulo_codigo));
  const codigoToProducto = {};
  (artData || [])?.forEach(a => { if (a?.producto_id) codigoToProducto[a.codigo] = a?.producto_id; });

  const productosConVentaReciente = new Set(
    Object.entries(codigoToProducto)
      .filter(([codigo]) => codigosConVenta.has(codigo))
      .map(([, pid]) => pid)
  );

  const resultado = (invData || [])?.filter(inv => inv?.producto?.activo && !productosConVentaReciente?.has(inv?.producto?.id))?.map(inv => {
      const coste = inv?.coste_medio || inv?.producto?.precio_compra || 0;
      const pvp = inv?.producto?.pvp_venta || 0;
      const stock = Math.max(inv?.stock_actual || 0, 0);
      return {
        producto_id: inv?.producto?.id,
        nombre: inv?.producto?.nombre,
        familia: inv?.producto?.categoria?.familia?.nombre,
        proveedor: inv?.producto?.proveedor?.nombre,
        stock,
        valor_coste: stock * coste,
        valor_pvp: stock * pvp,
      };
    })?.sort((a, b) => b?.valor_coste - a?.valor_coste);

  return resultado;
};

// ─── ALERTAS INTELIGENTES ─────────────────────────────────────────────────────
export const getAlertas = async (productosConRotacion, diasRecientes = 60) => {
  const alertas = [];

  for (const p of productosConRotacion) {
    const stock = p?.stock_actual || 0;
    const velocidad = p?.velocidad_dia || 0;
    const cobertura = p?.cobertura_dias;

    // 🔴 ROTURA REAL: vende y no tiene stock
    if (stock <= 0 && velocidad > 0) {
      alertas?.push({ tipo: 'ROTURA_REAL', nivel: 'critico', producto: p });
    }
    // 🟠 RIESGO ROTURA: cobertura < umbral
    else if (cobertura !== null && cobertura <= ROTATION_CONFIG?.cobertura?.riesgoRotura && stock > 0) {
      alertas?.push({ tipo: 'RIESGO_ROTURA', nivel: 'alto', producto: p });
    }
    // 🔵 SOBRE-STOCK
    else if (cobertura !== null && cobertura > ROTATION_CONFIG?.cobertura?.stockCorrecto) {
      alertas?.push({ tipo: 'SOBRE_STOCK', nivel: 'info', producto: p });
    }
    // 🟡 BAJA ROTACIÓN con stock
    else if (p?.clasificacion_rotacion === 'BAJA' && stock > 0) {
      alertas?.push({ tipo: 'BAJA_ROTACION', nivel: 'medio', producto: p });
    }
    // ⚫ SIN MOVIMIENTO con stock
    else if (p?.clasificacion_rotacion === 'SIN_MOVIMIENTO' && stock > 0) {
      alertas?.push({ tipo: 'SIN_MOVIMIENTO', nivel: 'bajo', producto: p });
    }
  }

  return alertas;
};

// ─── REPOSICIÓN SUGERIDA ──────────────────────────────────────────────────────
export const getReposicionSugerida = (productosConRotacion, diasObjetivo = 30) => {
  return productosConRotacion?.filter(p => {
      const stock = p?.stock_actual || 0;
      const velocidad = p?.velocidad_dia || 0;
      if (velocidad <= 0) return false;
      const cobertura = p?.cobertura_dias;
      return cobertura !== null && cobertura < diasObjetivo;
    })?.map(p => {
      const velocidad = p?.velocidad_dia || 0;
      const stock = p?.stock_actual || 0;
      const unidadesSugeridas = Math.ceil(velocidad * diasObjetivo - stock);
      return {
        ...p,
        unidades_sugeridas: Math.max(unidadesSugeridas, 0),
        dias_objetivo: diasObjetivo,
      };
    })?.sort((a, b) => (a?.cobertura_dias || 0) - (b?.cobertura_dias || 0));
};

// ─── STOCK INMOVILIZADO ───────────────────────────────────────────────────────
export const getStockInmovilizado = async () => {
  const thresholds = [30, 60, 90, 180];
  const results = {};

  for (const dias of thresholds) {
    const productos = await getProductosSinMovimiento(dias);
    results[`dias${dias}`] = {
      n_productos: productos?.length,
      unidades: productos?.reduce((s, p) => s + (p?.stock || 0), 0),
      valor_coste: productos?.reduce((s, p) => s + (p?.valor_coste || 0), 0),
      valor_pvp: productos?.reduce((s, p) => s + (p?.valor_pvp || 0), 0),
    };
  }

  return results;
};

// ─── FILTROS: FAMILIAS Y PROVEEDORES ─────────────────────────────────────────
export const getFiltrosOpciones = async () => {
  const [famRes, provRes, marcasRes] = await Promise.all([
    tiendaDb?.from('familias_producto')?.select('id, nombre')?.eq('activo', true)?.order('nombre'),
    tiendaDb?.from('proveedores')?.select('id, nombre')?.eq('activo', true)?.order('nombre'),
    tiendaDb?.from('marcas')?.select('id, nombre')?.eq('activo', true)?.order('nombre'),
  ]);
  return {
    familias: famRes?.data || [],
    proveedores: provRes?.data || [],
    marcas: marcasRes?.data || [],
  };
};
